import { Component, Inject, DestroyRef, inject } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { GestaoUsuario } from 'src/app/models/gestao-usuario'
import { GestaoUsuarioService } from 'src/app/services/gestao-usuario.service'
import { ConfirmDialogModel, ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

export interface DialogGestaoUsuarioData {
  usuario?: GestaoUsuario
  isSuperAdmin?: boolean
  currentUsername?: string
}

@Component({
  selector: 'app-dialog-gestao-usuario',
  templateUrl: './dialog-gestao-usuario.component.html',
  styleUrls: ['./dialog-gestao-usuario.component.scss'],
})
export class DialogGestaoUsuarioComponent {
  private destroyRef = inject(DestroyRef)

  isEdicao: boolean
  isProprioUsuario: boolean
  form: FormGroup

  allPapeis = [
    { value: 'CONCURSOADMIN', label: 'Administrador de Concursos' },
    { value: 'EXAMINADOR', label: 'Examinador' },
    { value: 'SG', label: 'SG' },
  ]

  get papeis() {
    let disponiveis = this.data.isSuperAdmin
      ? this.allPapeis
      : this.allPapeis.filter(p => p.value === 'CONCURSOADMIN')

    if (this.data.usuario?.papeis) {
      const cadastrados = this.data.usuario.papeis.map(p => p.nome)
      disponiveis = disponiveis.filter(p => !cadastrados.includes(p.value))
    }
    return disponiveis
  }

  constructor(
    private gestaoUsuarioService: GestaoUsuarioService,
    private unespCoreMessageService: UnespCoreMessageService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<DialogGestaoUsuarioComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogGestaoUsuarioData
  ) {
    this.isEdicao = !!data.usuario
    this.isProprioUsuario = !!data.usuario && data.usuario.email === data.currentUsername
    this.form = new FormGroup({
      username: new FormControl({ value: data.usuario?.username || '', disabled: this.isEdicao }, Validators.required),
      email: new FormControl(data.usuario?.email || '', [Validators.required, Validators.email]),
      codigoUnidade: new FormControl(data.usuario?.codigoUnidade || '', Validators.required),
      matricula: new FormControl(data.usuario?.matricula || '', Validators.required),
      nome: new FormControl(data.usuario?.nome || '', Validators.required),
      papel: new FormControl('', this.isEdicao ? [] : [Validators.required]),
    })
  }

  destacarBotaoPapel = false
  salvando = false

  private papelDescricoes: Record<string, string> = {
    CONCURSOADMIN: 'Administrador de Concursos — gerencia concursos da unidade',
    EXAMINADOR: 'Examinador — avalia candidatos em concursos',
    SG: 'Secretaria Geral — gestão multi-unidades',
    SUPERADMIN: 'Super Administrador — acesso total ao sistema',
  }

  getPapelDescricao(nome: string): string {
    return this.papelDescricoes[nome] || nome
  }

  salvar() {
    if (!this.form.valid) {
      this.unespCoreMessageService.showMessageError('Preencha todos os campos obrigatórios')
      return
    }

    const { username, email, codigoUnidade, matricula, nome, papel } = this.form.getRawValue()

    if (this.isEdicao && papel) {
      const confirmDialog = new ConfirmDialogModel(
        'Papel não cadastrado',
        `Você selecionou o papel "${papel}" mas não o adicionou. Deseja cadastrar este papel antes de atualizar?`,
        'Sim, adicionar papel',
        'Não, apenas atualizar'
      )
      const ref = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog })
      ref.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
        if (res) {
          this.destacarBotaoPapel = true
          setTimeout(() => this.destacarBotaoPapel = false, 2000)
        } else {
          this.executarAtualizacao(username, email, codigoUnidade, matricula, nome)
        }
      })
      return
    }

    if (this.isEdicao) {
      this.executarAtualizacao(username, email, codigoUnidade, matricula, nome)
    } else {
      this.salvando = true
      this.gestaoUsuarioService.criar(username, email, codigoUnidade, matricula, nome, papel).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.unespCoreMessageService.showMessageSuccess('Usuário criado com sucesso')
          this.dialogRef.close(true)
        },
        error: () => this.salvando = false
      })
    }
  }

  private executarAtualizacao(username: string, email: string, codigoUnidade: string, matricula: string, nome: string) {
    this.salvando = true
    this.gestaoUsuarioService.atualizar(username, email, codigoUnidade, matricula, nome).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.unespCoreMessageService.showMessageSuccess('Usuário atualizado com sucesso')
        this.dialogRef.close(true)
      },
      error: () => this.salvando = false
    })
  }

  adicionarPapel() {
    const papel = this.form.get('papel')?.value
    if (!papel || !this.data.usuario) return
    this.gestaoUsuarioService.adicionarPapel(this.data.usuario.username, papel).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.unespCoreMessageService.showMessageSuccess(`Papel ${papel} adicionado com sucesso`)
        this.data.usuario!.papeis = [...this.data.usuario!.papeis, { id: 0, nome: papel, descricao: '' }]
        this.form.get('papel')?.setValue('')
      },
      error: (err: any) => {
        if (err?.status === 409) {
          this.unespCoreMessageService.showMessageError('Limite de administradores atingido. Remova um administrador existente antes de adicionar novo.')
        }
      }
    })
  }
}
